import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal, Radio, Select, Space, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useCreateAssemblyMutation,
  useDeleteAssemblyMutation,
  useGetAssembliesQuery,
  useGetConstituenciesQuery,
  useGetStatesQuery,
  useImportAssembliesMutation,
  useUpdateAssemblyMutation,
} from '@/modules/locations/locationsApi';
import type { AssemblyMaster, CreateAssemblyPayload } from '@/types/location';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmPermanentLocationDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { useLocationBulkUpload } from './components/useLocationBulkUpload';
import styles from './Locations.module.scss';

type AssemblyFormValues = CreateAssemblyPayload;
type StatusFilter = 'all' | 'active' | 'inactive';

export default function Assemblies() {
  const notify = useNotify();
  const [form] = Form.useForm<AssemblyFormValues>();
  const selectedStateId = Form.useWatch('stateId', form);
  const { data, isLoading, isFetching, refetch } = useGetAssembliesQuery();
  const { data: statesData, isLoading: isStatesLoading } = useGetStatesQuery({ status: 'active' });
  const { data: formConstituenciesData, isLoading: isConstituenciesLoading } =
    useGetConstituenciesQuery(
      { stateId: selectedStateId, status: 'active' },
      { skip: !selectedStateId },
    );
  const { data: allConstituenciesData } = useGetConstituenciesQuery();
  const [createAssembly, { isLoading: isCreating }] = useCreateAssemblyMutation();
  const [updateAssembly, { isLoading: isUpdating }] = useUpdateAssemblyMutation();
  const [deleteAssembly, { isLoading: isDeleting }] = useDeleteAssemblyMutation();
  const [importAssemblies, { isLoading: isImporting }] = useImportAssembliesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAssembly, setEditingAssembly] = useState<AssemblyMaster | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [nameSearch, setNameSearch] = useState('');
  const [nameSearchDraft, setNameSearchDraft] = useState('');
  const [stateId, setStateId] = useState<string | undefined>();
  const [stateIdDraft, setStateIdDraft] = useState<string | undefined>();
  const [constituencyId, setConstituencyId] = useState<string | undefined>();
  const [constituencyIdDraft, setConstituencyIdDraft] = useState<string | undefined>();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [statusDraft, setStatusDraft] = useState<StatusFilter>('all');

  const hasActiveFilters =
    nameSearch.length > 0 || Boolean(stateId) || Boolean(constituencyId) || status !== 'all';

  const handleResetFilters = () => {
    setNameSearch('');
    setNameSearchDraft('');
    setStateId(undefined);
    setStateIdDraft(undefined);
    setConstituencyId(undefined);
    setConstituencyIdDraft(undefined);
    setStatus('all');
    setStatusDraft('all');
    setPage(1);
  };

  const { bulkUploadControls } = useLocationBulkUpload({
    entityLabel: 'Assembly',
    entityLabelPlural: 'Assemblies',
    exportPath: '/assemblies/export',
    templatePath: '/assemblies/export-template',
    exportFilename: 'assemblies.xlsx',
    templateFilename: 'assemblies-template.xlsx',
    importMutation: importAssemblies,
    isImporting,
    onImported: () => {
      void refetch();
    },
  });

  useGlobalLoader(isFetching && !isLoading);

  const assemblies = data?.results ?? [];
  const allConstituencies = allConstituenciesData?.results ?? [];
  const stateOptions = (statesData?.results ?? []).map((state) => ({
    label: state.name,
    value: state.id,
  }));
  const formConstituencyOptions = selectedStateId
    ? (formConstituenciesData?.results ?? []).map((constituency) => ({
        label: constituency.name,
        value: constituency.id,
      }))
    : [];

  const filterConstituencyOptions = useMemo(() => {
    const source = stateIdDraft
      ? allConstituencies.filter((item) => item.stateId === stateIdDraft)
      : allConstituencies;
    return source.map((item) => ({ label: item.name, value: item.id }));
  }, [allConstituencies, stateIdDraft]);

  const filteredAssemblies = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();
    return assemblies.filter((item) => {
      if (stateId && item.stateId !== stateId) return false;
      if (constituencyId && item.constituencyId !== constituencyId) return false;
      if (status === 'active' && !item.isActive) return false;
      if (status === 'inactive' && item.isActive) return false;
      if (!query) return true;
      return (
        item.name.toLowerCase().includes(query) || item.slug.toLowerCase().includes(query)
      );
    });
  }, [assemblies, nameSearch, stateId, constituencyId, status]);

  useEffect(() => {
    if (!modalOpen) return;
    form.setFieldsValue({
      stateId: editingAssembly?.stateId,
      constituencyId: editingAssembly?.constituencyId || undefined,
      name: editingAssembly?.name ?? '',
      isActive: editingAssembly?.isActive ?? true,
    });
  }, [editingAssembly, form, modalOpen]);

  const openCreate = () => {
    setEditingAssembly(null);
    setModalOpen(true);
  };

  const openEdit = (assembly: AssemblyMaster) => {
    setEditingAssembly(assembly);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAssembly(null);
    form.resetFields();
  };

  const handleSubmit = async (values: AssemblyFormValues) => {
    const body = { ...values, name: values.name.trim() };
    const result = await resolveMutationResult(
      editingAssembly
        ? updateAssembly({ id: editingAssembly.id, body })
        : createAssembly(body),
    );

    if (result.ok) {
      notify.success(result.message ?? (editingAssembly ? 'Assembly updated.' : 'Assembly created.'));
      closeModal();
      return;
    }

    notify.error(result.message);
  };

  const handleDelete = async (assembly: AssemblyMaster) => {
    const confirmed = await confirmPermanentLocationDelete(assembly.name);
    if (!confirmed) return;

    const result = await resolveMutationResult(deleteAssembly(assembly.id));
    if (result.ok) {
      notify.success(result.message ?? 'Assembly deleted.');
      return;
    }

    notify.error(result.message);
  };

  const columns: ColumnsType<AssemblyMaster> = [
    {
      title: 'ASSEMBLY',
      dataIndex: 'name',
      key: 'name',
      filterIcon: (filtered) => (
        <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: nameSearch ? [nameSearch] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setNameSearch(nameSearchDraft.trim());
            setPage(1);
            close();
          }}
          onReset={() => {
            setNameSearchDraft('');
            setNameSearch('');
            setPage(1);
            close();
          }}
        >
          <Input
            allowClear
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Search name or slug"
            value={nameSearchDraft}
            onChange={(event) => setNameSearchDraft(event.target.value)}
            onPressEnter={() => {
              setNameSearch(nameSearchDraft.trim());
              setPage(1);
              close();
            }}
          />
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setNameSearchDraft(nameSearch);
        },
      },
      render: (name: string, record) => (
        <div>
          <div className={styles.nameCell}>{name}</div>
          <div className={styles.subText}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'STATE',
      dataIndex: 'stateName',
      key: 'stateName',
      filterIcon: (filtered) => (
        <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: stateId ? [stateId] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setStateId(stateIdDraft);
            if (
              stateIdDraft &&
              constituencyId &&
              !allConstituencies.some(
                (item) => item.id === constituencyId && item.stateId === stateIdDraft,
              )
            ) {
              setConstituencyId(undefined);
              setConstituencyIdDraft(undefined);
            }
            setPage(1);
            close();
          }}
          onReset={() => {
            setStateIdDraft(undefined);
            setStateId(undefined);
            setPage(1);
            close();
          }}
        >
          <Select
            allowClear
            showSearch
            size="small"
            className={styles.filterSelect}
            placeholder="Select state"
            options={stateOptions}
            optionFilterProp="label"
            value={stateIdDraft}
            onChange={(value) => setStateIdDraft(value)}
          />
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setStateIdDraft(stateId);
        },
      },
      render: (stateName: string) => stateName || '-',
    },
    {
      title: 'CONSTITUENCY',
      dataIndex: 'constituencyName',
      key: 'constituencyName',
      filterIcon: (filtered) => (
        <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: constituencyId ? [constituencyId] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setConstituencyId(constituencyIdDraft);
            setPage(1);
            close();
          }}
          onReset={() => {
            setConstituencyIdDraft(undefined);
            setConstituencyId(undefined);
            setPage(1);
            close();
          }}
        >
          <Select
            allowClear
            showSearch
            size="small"
            className={styles.filterSelect}
            placeholder="Select constituency"
            options={filterConstituencyOptions}
            optionFilterProp="label"
            value={constituencyIdDraft}
            onChange={(value) => setConstituencyIdDraft(value)}
          />
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) {
            setConstituencyIdDraft(constituencyId);
            setStateIdDraft(stateId);
          }
        },
      },
      render: (constituencyName: string) => constituencyName || '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 140,
      filterIcon: (filtered) => (
        <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
      ),
      filteredValue: status !== 'all' ? [status] : null,
      filterDropdown: ({ close }) => (
        <ColumnFilterPanel
          onApply={() => {
            setStatus(statusDraft);
            setPage(1);
            close();
          }}
          onReset={() => {
            setStatusDraft('all');
            setStatus('all');
            setPage(1);
            close();
          }}
        >
          <Radio.Group
            value={statusDraft}
            onChange={(event) => setStatusDraft(event.target.value)}
            className={styles.statusRadio}
          >
            <Radio value="all">All</Radio>
            <Radio value="active">Active</Radio>
            <Radio value="inactive">Inactive</Radio>
          </Radio.Group>
        </ColumnFilterPanel>
      ),
      filterDropdownProps: {
        onOpenChange: (open) => {
          if (open) setStatusDraft(status);
        },
      },
      render: (isActive: boolean) => (
        <span className={isActive ? styles.statusActive : styles.statusInactive}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'UPDATED',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 140,
      align: 'center',
      render: (_value, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFilters ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
                Reset
              </Button>
            ) : null}
            {bulkUploadControls}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add assembly
            </Button>
          </>
        }
      />

      <div className={styles.tableCard}>
        <Table<AssemblyMaster>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={filteredAssemblies}
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </div>

      <Modal
        title={editingAssembly ? 'Edit Assembly' : 'Add Assembly'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        confirmLoading={isCreating || isUpdating}
        destroyOnClose
      >
        <Form<AssemblyFormValues>
          form={form}
          layout="vertical"
          initialValues={{ isActive: true }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="State"
            name="stateId"
            rules={[{ required: true, message: 'Please select state.' }]}
          >
            <Select
              showSearch
              loading={isStatesLoading}
              options={stateOptions}
              optionFilterProp="label"
              placeholder="Select state"
              onChange={() => form.setFieldsValue({ constituencyId: undefined })}
            />
          </Form.Item>
          <Form.Item
            label="Constituency"
            name="constituencyId"
            rules={[{ required: true, message: 'Please select constituency.' }]}
          >
            <Select
              showSearch
              disabled={!selectedStateId}
              loading={isConstituenciesLoading}
              options={formConstituencyOptions}
              optionFilterProp="label"
              placeholder={selectedStateId ? 'Select constituency' : 'Select state first'}
            />
          </Form.Item>
          <Form.Item
            label="Assembly"
            name="name"
            rules={[{ required: true, message: 'Please enter assembly name.' }]}
          >
            <Input placeholder="Enter assembly name" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
